import React, { useMemo, useCallback, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Col,
  Row,
  Table,
  DatePicker,
} from 'antd';
import { CalendarOutlined, PlusOutlined, ReloadOutlined, ToolOutlined, UserOutlined } from '@ant-design/icons';
import { DateTime } from 'luxon';
import moment from 'moment';
import PropTypes from 'prop-types';

import OnTraccrButton from '../../../../common/buttons/OnTraccrButton';
import useToggle from '../../../../common/hooks/useToggle';
import usePreloadHook from '../../../../common/hooks/usePreloadHook';
import TableDeleteButton from '../TableDeleteButton';
import TimeTrackingGridWizardAddMemberDrawer from './Wizard/TimeTrackingGridWizardAddMemberDrawer';
import TimeTrackingGridWizardAddCostcodeDrawer from './Wizard/TimeTrackingGridWizardAddCostcodeDrawer';
import {
  columnsToWizardCostcodeSelections,
  mergeCostcodeSelectionsIntoGridState,
} from './Wizard/buildWizardGridPayload';
import TimeTrackingGridColumnTitle from './TimeTrackingGridColumnTitle';
import TimeTrackingGridCell from './TimeTrackingGridCell';
import UnassignedUserPills from './UnassignedUserPills';
import UnassignedEquipmentPills from './UnassignedEquipmentPills';
import TimeTrackingGridRedistributeModal from './redistribution/TimeTrackingGridRedistributeModal';
import useTimeTrackingGridPreloadSectionParser from './TimeTrackingGridPreloadSectionParser';
import useTimeTrackingGridTaskPreload from './useTimeTrackingGridTaskPreload';
import { getIdMap } from '../../../../helpers/helpers';
import TimeTrackingGridSummaryCell from './TimeTrackingGridSummaryCell';
import {
  HOUR_VIEW_KEYS,
  showRegularHours,
  showOTHours,
  showDoubleOTHours,
  isSingleHourTypeView,
} from './timeTrackingGrid.constants';
import TimeTrackingGridWizard from './Wizard/TimeTrackingGridWizard';
import { formatDisplayHours } from './timeTrackingGridHelpers';

const extractHours = (datum) => ({
  hours: parseFloat(datum?.hours) || 0,
  otHours: parseFloat(datum?.otHours) || 0,
  doubleOTHours: parseFloat(datum?.doubleOTHours) || 0,
});

const isUnassignedColumn = (column) => !!column && !column.costcode?.id;

const sumCellHours = (relevantData) => (
  extractHours(relevantData).hours
  + extractHours(relevantData).otHours
  + extractHours(relevantData).doubleOTHours
);

const formatUnassignedHoursLabel = (total) => formatDisplayHours(total);

export default function TimeTrackingGridPreview({
  id,
  responses = {},
  previewProps = {},
  configProps = {},
  responding = false,
  divisions = [],
  setResponses,
  isDisplay = false,
  costcodes = [],
  fetchProjectCostcodes,
}) {
  const {
    [id]: {
      columns: responseColumns = [],
      values: responseValues = [],
      date: responseDate,
    } = {},
  } = responses;
  const {
    columns: previewColumns = [],
    values: previewRows = [],
    date: previewDate,
  } = previewProps;

  const [loading, setLoading] = useState(false);
  const [selectedView, setSelectedView] = useState('All');
  const [redistributeContext, setRedistributeContext] = useState(null);
  const [redistributeModalVisible, setRedistributeModalVisible] = useState(false);

  const { toggle: toggleUserDrawer, isToggled: showUserDrawer } = useToggle();
  const { toggle: toggleCostcodeDrawer, isToggled: showCostcodeDrawer } = useToggle();
  const { toggle: toggleEquipmentDrawer, isToggled: showEquipmentDrawer } = useToggle();

  // Redux selectors for preloading
  const users = useSelector((state) => state.users.users);
  const equipment = useSelector((state) => state.equipment.equipment);
  const reduxDivisions = useSelector((state) => state.settings.divisions);
  const { settings = {} } = useSelector((state) => state.settings.company);
  const phases = useSelector((state) => state.costcodes.phases);
  const projects = useSelector((state) => state.projects.projects);

  const divisionSet = useMemo(() => new Set(divisions), [divisions]);

  const divisionUsers = useMemo(() => {
    const allUsers = divisions.map((divisionId) => {
      const {
        [divisionId]: {
          users: divUsers = new Set(),
        } = {},
      } = reduxDivisions;
      return Array.from(divUsers);
    });
    const userSet = new Set(allUsers.flat());
    return users.filter((user) => userSet.has(user.id));
  }, [users, reduxDivisions, divisions]);
  const divisionUserMap = useMemo(() => getIdMap(divisionUsers), [divisionUsers]);

  const divisionEquipment = useMemo(() => equipment.filter(
    (eq) => eq.active && eq.divisionIds?.some((divisionId) => divisionSet.has(divisionId)),
  ), [equipment, divisionSet]);
  const divisionEquipmentMap = useMemo(() => getIdMap(divisionEquipment), [divisionEquipment]);

  const divisionCostcodes = useMemo(() => costcodes.filter((cc) => (
    cc.active && cc.divisionId && divisionSet.has(cc.divisionId)
  )), [costcodes, divisionSet]);
  const divisionCostcodeMap = useMemo(() => getIdMap(divisionCostcodes), [divisionCostcodes]);

  const projectIdMap = useMemo(() => getIdMap(projects), [projects]);
  const phaseIdMap = useMemo(() => getIdMap(phases), [phases]);

  const sectionParser = useTimeTrackingGridPreloadSectionParser({
    divisionUserMap,
    divisionEquipmentMap,
    divisionCostcodeMap,
  });

  const preloadFromTimeEntries = configProps?.timeTrackingGridPreloadSource === 'timeEntries';

  const { triggerPreload } = usePreloadHook({
    responses,
    id,
    setResponses,
    columns: responseColumns,
    responding,
    configProps,
    type: 'TimeTrackingGrid',
    setLoading,
    divisions,
    costcodeMap: divisionCostcodeMap,
    sectionParser,
    isTimeTrackingGrid: true,
    skipPreload: preloadFromTimeEntries,
  });

  const { triggerTaskPreload } = useTimeTrackingGridTaskPreload({
    responses,
    id,
    setResponses,
    responding,
    configProps,
    setLoading,
    divisionUserMap,
    divisionCostcodeMap,
    divisionEquipmentMap,
    projectIdMap,
    phaseIdMap,
    columns: responseColumns,
  });

  const hasGridContent = useMemo(
    () => (responseColumns?.length > 0) || (responseValues?.length > 0),
    [responseColumns, responseValues],
  );

  const onReloadPreload = useCallback(() => {
    const currentValues = responses[id]?.values || [];
    const hasNonPreloadedValues = currentValues.some((v) => !v.isPreloaded);
    if (hasNonPreloadedValues) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        'You have manually entered data. Reloading will replace it with preloaded data. Continue?',
      );
      if (!confirmed) return;
    }
    if (preloadFromTimeEntries) {
      triggerTaskPreload();
    } else {
      triggerPreload();
    }
  }, [responses, id, triggerPreload, triggerTaskPreload, preloadFromTimeEntries]);

  const onDeleteColumn = useCallback((columnRowId) => {
    if (!setResponses) return;
    setResponses((prev) => {
      const currentResponse = prev[id] || {};
      const updatedColumns = (currentResponse.columns || [])
        .filter((col) => col.rowId !== columnRowId);
      const updatedValues = (currentResponse.values || []).map((value) => {
        const { [columnRowId]: removed, ...rest } = value;
        return rest;
      });
      return {
        ...prev,
        [id]: {
          ...currentResponse,
          columns: updatedColumns,
          values: updatedValues,
        },
      };
    });
  }, [id, setResponses]);

  const onDeleteRow = useCallback((rowId) => {
    if (!setResponses) return;
    setResponses((prev) => {
      const currentResponse = prev[id] || {};
      const updatedValues = (currentResponse.values || [])
        .filter((value) => value.id !== rowId);
      return {
        ...prev,
        [id]: {
          ...currentResponse,
          values: updatedValues,
        },
      };
    });
  }, [id, setResponses]);

  const onCellChange = useCallback((rowId, columnKey, newData) => {
    if (!setResponses) return;
    setResponses((prev) => {
      const currentResponse = prev[id] || {};
      const updatedValues = (currentResponse.values || []).map((value) => {
        if (value.id !== rowId) return value;
        return {
          ...value,
          [columnKey]: newData,
        };
      });
      return {
        ...prev,
        [id]: {
          ...currentResponse,
          values: updatedValues,
        },
      };
    });
  }, [id, setResponses]);

  const onDateChange = useCallback((newDateMoment) => {
    if (!setResponses) return;
    const timezone = DateTime.local().zoneName;
    const newDate = newDateMoment
      ? DateTime.fromJSDate(newDateMoment.toDate(), { zone: timezone }).toISODate()
      : null;
    setResponses((prev) => ({
      ...prev,
      [id]: {
        ...(prev[id] || {}),
        date: newDate,
        timezone,
      },
    }));
  }, [id, setResponses]);

  const relevantColumns = responding ? responseColumns : previewColumns;
  const relevantRows = responding ? responseValues : previewRows;

  const selectedUserRows = useMemo(
    () => (relevantRows || []).filter((row) => row.type === 'user'),
    [relevantRows],
  );
  const selectedEquipmentRows = useMemo(
    () => (relevantRows || []).filter((row) => row.type === 'equipment'),
    [relevantRows],
  );
  const wizardCostcodeSelections = useMemo(
    () => columnsToWizardCostcodeSelections(relevantColumns || []),
    [relevantColumns],
  );

  const updateRows = (type, rows) => {
    if (!setResponses) return;
    setResponses((prev) => {
      const currentResponse = prev[id] || {};
      const values = currentResponse.values || [];
      const valuesToKeep = values.filter((v) => v.type !== type);
      const oldRowMap = getIdMap(values.filter((v) => v.type === type));
      const newRows = rows.map((row) => ({
        ...(oldRowMap[row.id] || {}),
        ...row,
      }));
      return {
        ...prev,
        [id]: {
          ...currentResponse,
          values: [...valuesToKeep, ...newRows],
        },
      };
    });
  };

  const commitPreviewUserRows = (rows) => (
    updateRows('user', rows)
  );

  const commitPreviewEquipmentRows = (rows) => (
    updateRows('equipment', rows)
  );

  const commitPreviewCostcodeSelections = useCallback((selections) => {
    if (!setResponses) return;
    setResponses((prev) => {
      const currentResponse = prev[id] || {};
      const { columns, values } = mergeCostcodeSelectionsIntoGridState({
        prevColumns: currentResponse.columns || [],
        prevValues: currentResponse.values || [],
        selections,
      });
      return {
        ...prev,
        [id]: {
          ...currentResponse,
          columns,
          values,
        },
      };
    });
  }, [id, setResponses]);

  const wizardCostcodeConfigProps = useMemo(() => ({
    isProjectRequired: !!configProps?.isProjectRequired,
    isCostcodeRequired: !!configProps?.isCostcodeRequired,
  }), [configProps?.isProjectRequired, configProps?.isCostcodeRequired]);

  const openRedistributeModal = useCallback((rowId, columnRowId) => {
    setRedistributeContext({ rowId, columnRowId });
    setRedistributeModalVisible(true);
  }, []);

  const closeRedistributeModal = useCallback(() => {
    setRedistributeModalVisible(false);
    setRedistributeContext(null);
  }, []);

  const tableColumns = useMemo(() => {
    const columns = relevantColumns?.map((column) => {
      const {
        project,
        phase,
        costcode,
      } = column;
      const unassignedColumn = isUnassignedColumn(column);
      const compact = isSingleHourTypeView(selectedView);

      return {
        title: (
          <TimeTrackingGridColumnTitle
            project={projectIdMap[project?.id] || project}
            phase={phase}
            costcode={costcode}
            responding={responding}
            isUnassigned={unassignedColumn}
            settings={settings}
            onDelete={() => onDeleteColumn(column.rowId)}
          />
        ),
        dataIndex: column.rowId,
        key: column.rowId,
        width: 250,
        onHeaderCell: () => ({
          className: unassignedColumn ? 'time-tracking-grid-column-header-unassigned' : '',
        }),
        className: unassignedColumn ? 'time-tracking-grid-column-cell-unassigned' : '',
        render: (data, datum) => {
          if (datum.isSummary) {
            if (unassignedColumn) {
              const totalToAssign = sumCellHours(data);
              return (
                <div className="ttg-to-assign-summary">
                  <span className="ttg-to-assign-summary-label">To Assign</span>
                  <span className="ttg-to-assign-summary-value">
                    {formatUnassignedHoursLabel(totalToAssign)}
                  </span>
                </div>
              );
            }
            return (
              <TimeTrackingGridSummaryCell
                datum={extractHours(data)}
                type={datum.type}
                selectedView={selectedView}
              />
            );
          }

          if (unassignedColumn) {
            const rowType = datum.type === 'equipment' ? 'equipment' : 'user';
            if (rowType === 'user') {
              return (
                <UnassignedUserPills
                  relevantData={data}
                  selectedView={selectedView}
                  responding={responding && !isDisplay}
                  compact={compact}
                  onPress={() => openRedistributeModal(datum.id, column.rowId)}
                />
              );
            }
            return (
              <UnassignedEquipmentPills
                relevantData={data}
                responding={responding && !isDisplay}
                compact={compact}
                onPress={() => openRedistributeModal(datum.id, column.rowId)}
              />
            );
          }

          return (
            <TimeTrackingGridCell
              data={data}
              type={datum.type}
              responding={responding}
              selectedView={selectedView}
              onChange={(newData) => onCellChange(datum.id, column.rowId, newData)}
            />
          );
        },
      };
    }) || [];

    columns.unshift({
      title: (
        <div className="time-tracking-grid-summary-row-name-date">
          <CalendarOutlined className="time-tracking-grid-summary-row-name-date-icon" />
          {
            responding ? (
              <DatePicker
                value={responseDate ? moment(responseDate) : null}
                onChange={onDateChange}
                format="MMM D, YYYY"
                allowClear
                placeholder="Select date"
              />
            ) : (
              <span>
                {previewDate ?? 'No date selected'}
              </span>
            )
          }
        </div>
      ),
      key: 'name',
      dataIndex: 'name',
      render: (name, datum) => {
        if (!datum.isSummary) {
          return (
            <Row>
              <Col span={16}>
                <div className="time-tracking-grid-row-name-text">{name}</div>
              </Col>
              {datum.type === 'user' && (
                <Col span={8} className="time-tracking-grid-row-name-badges">
                  {showRegularHours(selectedView) && (
                    <div className="time-tracking-grid-reg">REG</div>
                  )}
                  {showOTHours(selectedView) && (
                    <div className="time-tracking-grid-ot">OT</div>
                  )}
                  {showDoubleOTHours(selectedView) && (
                    <div className="time-tracking-grid-dt">DT</div>
                  )}
                </Col>
              )}
            </Row>
          );
        }

        return (
          <div className="time-tracking-grid-summary-row-name">
            <div className={`${datum.id === 'users' ? 'time-tracking-grid-summary-row-name-icon-div-user' : 'time-tracking-grid-summary-row-name-icon-div-equipment'}`}>
              {datum.id === 'users' ? (
                <UserOutlined className="time-tracking-grid-summary-row-name-icon-user" />
              ) : (
                <ToolOutlined className="time-tracking-grid-summary-row-name-icon-equipment" />
              )}
            </div>
            <div className="time-tracking-grid-summary-row-name-text">{name}</div>
          </div>
        );
      },
    });

    // Add total column that sums all costcode columns for each row
    columns.push({
      title: <span style={{ fontWeight: 600 }}>Total</span>,
      key: 'total',
      width: 200,
      render: (_, datum) => {
        // Calculate total across all costcode columns
        const total = relevantColumns?.reduce((acc, col) => {
          const colData = datum[col.rowId] || {};
          return {
            hours: acc.hours + (parseFloat(colData.hours) || 0),
            otHours: acc.otHours + (parseFloat(colData.otHours) || 0),
            doubleOTHours: acc.doubleOTHours + (parseFloat(colData.doubleOTHours) || 0),
          };
        }, {
          hours: 0,
          otHours: 0,
          doubleOTHours: 0,
        }) || { hours: 0, otHours: 0, doubleOTHours: 0 };

        const itemLabel = datum.type === 'user' ? 'User' : 'Equipment';

        if (datum.isSummary) {
          return (
            <TimeTrackingGridSummaryCell
              datum={total}
              type={datum.type}
              selectedView={selectedView}
            />
          );
        }

        const deleteButton = (responding && !isDisplay) ? (
          <TableDeleteButton
            onConfirm={() => onDeleteRow(datum.id)}
            title={`Remove ${itemLabel}`}
            content={`Are you sure you want to remove "${datum.name}"? Any hours entered for this ${datum.type} will be lost.`}
            okText="Remove"
          />
        ) : null;

        if (datum.type === 'equipment') {
          return (
            <div className="time-tracking-grid-total-cell">
              <span className="time-tracking-grid-total-cell-hours">{formatDisplayHours(total.hours)}</span>
              {deleteButton}
            </div>
          );
        }

        return (
          <Row>
            <Col span={18} className="time-tracking-grid-row-total-column">
              {showRegularHours(selectedView) && (
                <div className="time-tracking-grid-total-cell-hours-container">
                  <div className="time-tracking-grid-total-cell-hours">
                    {formatDisplayHours(total.hours)}
                  </div>
                  <div className="time-tracking-grid-reg">REG</div>
                </div>
              )}
              {showOTHours(selectedView) && (
                <div className="time-tracking-grid-total-cell-hours-container">
                  <div className="time-tracking-grid-total-cell-hours">
                    {formatDisplayHours(total.otHours)}
                  </div>
                  <div className="time-tracking-grid-ot">OT</div>
                </div>
              )}
              {showDoubleOTHours(selectedView) && (
                <div className="time-tracking-grid-total-cell-hours-container">
                  <div className="time-tracking-grid-total-cell-hours">
                    {formatDisplayHours(total.doubleOTHours)}
                  </div>
                  <div className="time-tracking-grid-dt">DT</div>
                </div>
              )}
            </Col>
            <Col span={6}>
              {deleteButton}
            </Col>
          </Row>
        );
      },
    });

    return columns;
  }, [
    relevantColumns,
    responding,
    onDeleteColumn,
    onDeleteRow,
    onCellChange,
    responseDate,
    selectedView,
    previewDate,
    onDateChange,
    isDisplay,
    openRedistributeModal,
    settings,
    projectIdMap,
  ]);

  const tableRows = useMemo(() => {
    const userRows = [];
    const equipmentRows = [];

    relevantRows?.forEach((value) => {
      if (value.type === 'user') {
        userRows.push(value);
      } else if (value.type === 'equipment') {
        equipmentRows.push(value);
      }
    });

    if (userRows.length) {
      const baseHeader = {
        id: 'users',
        isSummary: true,
        name: 'Users',
      };

      tableColumns.forEach((column) => {
        if (column.key === 'name') return;
        baseHeader[column.key] = userRows.reduce((acc, row) => {
          const relevantData = row[column.key] || {};
          return {
            hours: acc.hours + extractHours(relevantData).hours,
            otHours: acc.otHours + extractHours(relevantData).otHours,
            doubleOTHours: acc.doubleOTHours + extractHours(relevantData).doubleOTHours,
          };
        }, {
          hours: 0,
          otHours: 0,
          doubleOTHours: 0,
        });
      });

      userRows.unshift(baseHeader);
    }

    if (equipmentRows.length) {
      const baseHeader = {
        id: 'equipment',
        type: 'equipment',
        isSummary: true,
        name: 'Equipment',
      };

      tableColumns.forEach((column) => {
        if (column.key === 'name') return;
        baseHeader[column.key] = equipmentRows.reduce((acc, row) => {
          const relevantData = row[column.key] || {};
          return {
            hours: acc.hours + extractHours(relevantData).hours,
            otHours: acc.otHours + extractHours(relevantData).otHours,
            doubleOTHours: acc.doubleOTHours + extractHours(relevantData).doubleOTHours,
          };
        }, {
          hours: 0,
          otHours: 0,
          doubleOTHours: 0,
        });
      });

      equipmentRows.unshift(baseHeader);
    }

    return [...userRows, ...equipmentRows];
  }, [relevantRows, tableColumns]);

  if (responding && !isDisplay && !configProps?.preloadExistingEntries && !hasGridContent) {
    return (
      <TimeTrackingGridWizard
        id={id}
        divisions={divisions}
        configProps={configProps}
        setResponses={setResponses}
        costcodes={costcodes}
        fetchProjectCostcodes={fetchProjectCostcodes}
      />
    );
  }

  return (
    <>
      {!isDisplay && (
        <Row align="middle" justify="space-between" style={{ width: '100%', marginBottom: 10 }}>
          <Col>
            <div className="time-tracking-grid-view-buttons">
              {HOUR_VIEW_KEYS.map((view) => (
                <div
                  key={view}
                  className={`time-tracking-grid-view-button ${selectedView === view ? 'active' : ''}`}
                  onClick={() => setSelectedView(view)}
                >
                  {view}
                </div>
              ))}
              </div>
          </Col>
          <Col>
            <Row gutter={10}>
              <Col>
                <OnTraccrButton
                  title="Add Users"
                  icon={<PlusOutlined />}
                  onClick={toggleUserDrawer}
                />
              </Col>
              <Col>
                <OnTraccrButton
                  title="Add Equipment"
                  icon={<PlusOutlined />}
                  onClick={toggleEquipmentDrawer}
                />
              </Col>
              <Col>
                <OnTraccrButton
                  title="Add Costcode"
                  icon={<PlusOutlined />}
                  onClick={toggleCostcodeDrawer}
                />
              </Col>
              {responding && configProps?.preloadExistingEntries && (
                <Col>
                  <OnTraccrButton
                    title="Reload Preloaded Data"
                    icon={<ReloadOutlined spin={loading} />}
                    onClick={onReloadPreload}
                    disabled={loading}
                  />
                </Col>
              )}
            </Row>
          </Col>
        </Row>
      )}
      <Table
        columns={tableColumns}
        dataSource={tableRows}
        rowKey="id"
        size="small"
        pagination={false}
        rowClassName={(record) => {
          if (record.isSummary) return 'time-tracking-grid-summary-row';
          return '';
        }}
        className="time-tracking-grid-table"
        onHeaderRow={() => ({
          className: 'time-tracking-grid-table-header-row',
        })}
        bordered
      />
      <TimeTrackingGridWizardAddMemberDrawer
        variant="user"
        visible={showUserDrawer}
        onClose={toggleUserDrawer}
        divisions={divisions}
        selectedRows={selectedUserRows}
        setSelectedRows={setResponses ? commitPreviewUserRows : null}
      />
      <TimeTrackingGridWizardAddMemberDrawer
        variant="equipment"
        visible={showEquipmentDrawer}
        onClose={toggleEquipmentDrawer}
        divisions={divisions}
        selectedRows={selectedEquipmentRows}
        setSelectedRows={setResponses ? commitPreviewEquipmentRows : null}
      />
      <TimeTrackingGridWizardAddCostcodeDrawer
        visible={showCostcodeDrawer}
        onClose={toggleCostcodeDrawer}
        selectedCostcodes={wizardCostcodeSelections}
        setSelectedCostcodes={setResponses ? commitPreviewCostcodeSelections : null}
        divisions={divisions}
        configProps={wizardCostcodeConfigProps}
        costcodes={costcodes}
        fetchProjectCostcodes={fetchProjectCostcodes}
      />
      {setResponses && (
        <TimeTrackingGridRedistributeModal
          id={id}
          responses={responses}
          setResponses={setResponses}
          visible={redistributeModalVisible}
          columnRowId={redistributeContext?.columnRowId}
          initialRowId={redistributeContext?.rowId}
          onClose={closeRedistributeModal}
          divisions={divisions}
          configProps={wizardCostcodeConfigProps}
          costcodes={costcodes}
          fetchProjectCostcodes={fetchProjectCostcodes}
        />
      )}
    </>
  );
}

TimeTrackingGridPreview.propTypes = {
  id: PropTypes.string.isRequired,
  responses: PropTypes.shape({
    [PropTypes.string]: PropTypes.shape({
      columns: PropTypes.arrayOf(PropTypes.shape({})),
      values: PropTypes.arrayOf(PropTypes.shape({})),
    }),
  }).isRequired,
  configProps: PropTypes.shape({
    title: PropTypes.string,
    optional: PropTypes.bool,
    preloadExistingEntries: PropTypes.bool,
    preloadProject: PropTypes.string,
    preloadForm: PropTypes.oneOfType([
      PropTypes.string,
      PropTypes.arrayOf(PropTypes.string),
    ]),
    preloadDropdown: PropTypes.string,
    preloadFormDropdown: PropTypes.string,
    preloadDateField: PropTypes.string,
    timeTrackingGridPreloadSource: PropTypes.oneOf(['form', 'timeEntries']),
  }),
  previewProps: PropTypes.shape({
    columns: PropTypes.arrayOf(PropTypes.shape({})),
    values: PropTypes.arrayOf(PropTypes.shape({})),
  }),
  responding: PropTypes.bool,
  isDisplay: PropTypes.bool,
  setResponses: PropTypes.func,
  divisions: PropTypes.arrayOf(PropTypes.string),
  costcodes: PropTypes.arrayOf(PropTypes.shape({})),
  fetchProjectCostcodes: PropTypes.func,
};

TimeTrackingGridPreview.defaultProps = {
  responding: false,
  isDisplay: false,
  previewProps: {},
  configProps: {},
  setResponses: null,
  divisions: [],
  costcodes: [],
  fetchProjectCostcodes: null,
};
